import React, { createRef, RefObject, useEffect, useRef, useState } from 'react'
import { DebounceInput } from 'react-debounce-input'
import { useOutside } from '@pacote/react-use-outside'
import { navigate } from '@reach/router'
import AbortController from 'abort-controller'
import Scrollbars from 'react-custom-scrollbars'
import { SearchResultItem } from './SearchResultItem'
import { LoadingBar } from '../loading-bar/LoadingBar'
import './search.scss'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'

declare global {
  interface Window {
    __FLEXSEARCH__: {
      readonly [language: string]: {
        readonly index: any
        readonly store: {
          readonly [key: string]: any
        }
      }
    }
  }
}

export interface SearchResult {
  readonly title: string
  readonly url: string
  readonly excerpt: string
  readonly content: string
  readonly section: string
  readonly chapter: string
  isOther?: boolean
  sectionStartLabel?: string
}

export interface SearchQuery {
  query: {
    query: string
    limit?: number
    threshold?: number
    resolution?: number
    depth?: number
  }
  category?: string
}

const useKeyPress = (targetKey: string, ref: RefObject<HTMLInputElement>) => {
  const [keyPressed, setKeyPressed] = useState(false)

  const downHandler = ({ key }: { key: string }) => {
    if (key === targetKey) {
      setKeyPressed(true)
      // to allow pressed key send events
      setTimeout(() => {
        setKeyPressed(false)
      })
    }
  }

  const upHandler = ({ key }: { key: string }) => {
    if (key === targetKey) {
      setKeyPressed(false)
    }
  }

  useEffect(() => {
    if (ref.current === null) {
      return
    }
    const currentRef = ref.current
    currentRef.addEventListener('keydown', downHandler)
    currentRef.addEventListener('keyup', upHandler)

    return () => {
      currentRef.removeEventListener('keydown', downHandler)
      currentRef.removeEventListener('keyup', upHandler)
    }
  })

  return keyPressed
}

interface SearchProps {
  readonly productName?: string
  readonly autoHeightMax?: string
  readonly searchQuery?: string
}

const Search = ({ productName, autoHeightMax, searchQuery }: SearchProps) => {
  const [query, setQuery] = useState(searchQuery || '')
  const [hasOtherProductData, setHasOtherProductData] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  // state of search results list
  const [isActive, setActive] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const searchBox = createRef<HTMLInputElement>()
  // set position of active element of search results list (defaults to -1)
  const [cursor, setCursor] = useState(-1)
  const downPress = useKeyPress('ArrowDown', searchBox)
  const upPress = useKeyPress('ArrowUp', searchBox)
  const enterPress = useKeyPress('Enter', searchBox)
  const searchRef = useOutside<HTMLDivElement>('mousedown', () => {
    setActive(false)
    setCursor(-1)
  })
  const scrollRef = useRef<Scrollbars>(null)
  const refsCollection: HTMLDivElement[] = []

  const scrollDown = (rect: DOMRect) => {
    if (scrollRef.current === null) {
      return
    }
    let topOffset = 0
    if (searchBox.current !== null) {
      topOffset = searchBox.current.getBoundingClientRect().bottom
    }
    const { scrollTop, clientHeight } = scrollRef.current.getValues()
    const top = scrollTop - clientHeight + rect.bottom - topOffset
    scrollRef.current.scrollTop(top)
  }

  const scrollUp = (rect: DOMRect, cursor: number) => {
    if (scrollRef.current === null) {
      return
    }
    let topOffset = 0
    if (searchBox.current !== null) {
      topOffset = searchBox.current.getBoundingClientRect().bottom
    }
    const { scrollTop, clientHeight } = scrollRef.current.getValues()
    const top = scrollTop - clientHeight + rect.bottom - topOffset
    if (rect.top <= topOffset) {
      let refsHeight = 0
      for (let i = 0; i < cursor; i++) {
        refsHeight += refsCollection[i].getBoundingClientRect().height
      }
      scrollRef.current.scrollTop(refsHeight)
    } else if (rect.bottom >= clientHeight + topOffset) {
      scrollRef.current.scrollTop(top)
    }
  }

  useEffect(() => {
    // if input field not focused exit
    if (!isActive) {
      return
    }

    const abortController = new AbortController()

    const queryString = query.trim()

    if (!queryString || queryString.length < 3) {
      setLoading(false)
      setResults([])
      setHasOtherProductData(false)
      return
    }
    const searchQuery: SearchQuery = {
      query: {
        query: queryString,
        threshold: 1,
        resolution: 3,
        depth: 3,
      },
    }

    // if (productName !== undefined) {
    //   searchQuery.category = productName
    // }

    setLoading(true)

    // 'http://localhost:8080/api/v1/search' for local development
    // eslint-disable-next-line no-undef
    fetch('/api/v1/search', {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(searchQuery),
      signal: abortController.signal,
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (abortController.signal.aborted) {
          return
        }
        setHasOtherProductData(false)
        if (productName !== undefined) {
          const productData: SearchResult[] = []
          const otherData: SearchResult[] = []
          data.forEach((result: SearchResult) => {
            const resultProduct = result.url.replace(/^\//, '').split('/')[0]
            if (resultProduct === productName) {
              productData.push(result)
            } else {
              result.isOther = true
              otherData.push(result)
            }
          })
          if (productData[0]) productData[0].sectionStartLabel = productName
          if (otherData[0]) {
            otherData[0].sectionStartLabel = 'Other Products'
            setHasOtherProductData(true)
          }
          setResults([...productData, ...otherData])
        } else {
          setResults(data)
        }

        setActive(true)
        setLoading(false)
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setLoading(false)
          throw err
        }
      })

    // Trigger the abortion in useEffect's cleanup function
    return () => {
      abortController.abort()
    }
  }, [query, productName, isActive])

  useEffect(() => {
    if (results.length && downPress) {
      setCursor((prevState) => {
        const cursor = prevState < results.length - 1 ? prevState + 1 : 0
        scrollDown(refsCollection[cursor].getBoundingClientRect())
        return cursor
      })
    }
  }, [downPress, results])
  useEffect(() => {
    if (results.length && upPress) {
      setCursor((prevState) => {
        const cursor = prevState > 0 ? prevState - 1 : results.length - 1
        scrollUp(refsCollection[cursor].getBoundingClientRect(), cursor)
        return cursor
      })
    }
  }, [upPress, results])
  useEffect(() => {
    if (results.length && enterPress && results[cursor] !== undefined) {
      navigate(results[cursor].url, { state: { query: query } })
      setActive(false)
    }
  }, [cursor, enterPress, results])

  useEffect(() => {
    if (searchBox.current !== null && isActive) {
      // Moving cursor to the end of input text
      searchBox.current.selectionStart = searchBox.current.value?.length
      searchBox.current.selectionEnd = searchBox.current.value?.length
    }
  }, [isActive])

  const breakpoints = useBreakpoint()

  return (
    <div ref={searchRef} className={`la-search ${isActive ? 'la-search_active' : ''}`}>
      <div className="search__input">
        {searchBox && searchBox.current ? searchBox.current.checked : null}
        <DebounceInput
          className={'search__input-field'}
          type="search"
          debounceTimeout={250}
          value={query}
          inputRef={searchBox}
          onChange={(event) => {
            query !== event.target.value.trim() && setQuery(event.target.value)
            setCursor(-1)
          }}
          onClick={() => {
            setActive(true)
            setCursor(-1)
          }}
          placeholder={
            !breakpoints.sm
              ? 'Find answers (User Guide, Web UI, Specifications…)'
              : 'Find answers (User Guide, Web UI…)'
          }
        />

        <i className="search__input-icon la-icon la-icon_search" />
      </div>

      {isLoading && <LoadingBar />}

      {isActive && query.length > 2 && (
        <Scrollbars
          autoHeight={true}
          autoHeightMax={autoHeightMax}
          className="la-search-result-list la-scrollbar"
          ref={scrollRef}
        >
          {results.length > 0 &&
            results.map((page, i) => (
              <React.Fragment key={`fragment-${i}`}>
                {page.sectionStartLabel && hasOtherProductData && (
                  <div className={'la-search-result-list__sub-header'} key={page.sectionStartLabel}>
                    {page.sectionStartLabel}
                  </div>
                )}
                <div
                  key={i}
                  className={`la-search-result-list__item${
                    i === cursor ? ' la-search-result-list__item_active' : ''
                  }`}
                  ref={(resultRef: HTMLDivElement) => (refsCollection[i] = resultRef)}
                  role={'button'}
                  tabIndex={i}
                  onKeyPress={() => undefined}
                  onClick={() => {
                    setActive(false)
                  }}
                >
                  <SearchResultItem
                    page={page}
                    showProductTag={!!productName && !page.isOther}
                    query={query}
                  />
                </div>
              </React.Fragment>
            ))}
          {!isLoading && results.length === 0 && (
            <p className={'la-search-result-list__no-data-message'}>
              No results found for query &#34;{query}&#34;
            </p>
          )}
        </Scrollbars>
      )}
    </div>
  )
}

export default Search
